import React, { useState } from "react";
import CameraCaptureButton from "@/components/CameraCaptureButton";
import { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const VIEWS = [
  { k: "w", label: "W", title: "Wide" },
  { k: "m", label: "M", title: "Medium" },
  { k: "c", label: "C", title: "Close-up" },
];

export default function WMCBlock({ block, value = {}, onChange, onCapture, photos = [] }) {
  const [viewer, setViewer] = useState(null);

  const toggle = (itemK, viewK) => {
    const cur = value[itemK] || {};
    onChange({ ...value, [itemK]: { ...cur, [viewK]: !cur[viewK] } });
  };

  const nextView = (v) => VIEWS.find((vw) => !v[vw.k]);

  const capture = async (it, file) => {
    const v = value[it.k] || {};
    const nv = nextView(v);
    const rec = await onCapture(file, { area: it.label, view: nv ? nv.label : "", notes: block.title || "" });
    if (rec && nv) onChange({ ...value, [it.k]: { ...v, [nv.k]: true } });
  };

  return (
    <div className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 print-avoid-break">
      {block.title && <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-foreground">{block.title}</h4>}
      <div className="grid grid-cols-1 gap-1.5">
        {block.items.map((it) => {
          const v = value[it.k] || {};
          const nv = nextView(v);
          const itemPhotos = photos.filter((p) => p.area === it.label);
          return (
            <div key={it.k} className="flex flex-col gap-2 rounded-lg px-3 py-2.5 hover:bg-secondary/50 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <span className="text-sm text-foreground">{it.label}</span>
                {itemPhotos.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {itemPhotos.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`wmc-thumb-${block.key}-${it.k}-${p.photo_id}`}
                        onClick={() => setViewer(p)}
                        className="relative h-10 w-10 overflow-hidden rounded-md border border-border tap-scale hover:border-primary"
                        title={`${p.photo_id}${p.view ? " · " + p.view : ""}`}
                      >
                        <img src={fileUrl(p.storage_path)} alt={p.photo_id} className="h-full w-full object-cover" />
                        {p.view && <span className="absolute bottom-0 right-0 bg-primary px-1 font-mono text-[8px] font-bold text-primary-foreground">{p.view}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <CameraCaptureButton
                  testid={`wmc-cam-${block.key}-${it.k}`}
                  variant="text"
                  label={nv ? `Take ${nv.label}` : "Done"}
                  disabled={!nv}
                  onCapture={(file) => capture(it, file)}
                />
                {VIEWS.map((vw) => (
                  <button
                    key={vw.k}
                    data-testid={`wmc-${block.key}-${it.k}-${vw.k}`}
                    title={vw.title}
                    onClick={() => toggle(it.k, vw.k)}
                    className={`h-8 w-8 rounded-md border font-mono text-xs font-bold tap-scale ${
                      v[vw.k]
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-secondary/40 text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    {vw.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-mono">{viewer?.photo_id} · {viewer?.area}{viewer?.view ? ` · ${viewer.view}` : ""}</DialogTitle></DialogHeader>
          {viewer && <img src={fileUrl(viewer.storage_path)} alt="" className="max-h-[70vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
